import { IUser, IUserRegister } from 'entities';
import {
  IBaseUserDataSimpleViewModel,
  IBaseUserViewModel,
  ICorrespondentResultViewModel,
  IDashboardStatisticsViewModal,
  IFinancialReportViewModel,
  IJobValuesViewModel,
  ILoginViewModel,
  INotificationPreferencesResponseViewModel,
  IPendencySolicitationViewModel,
  ISearchViewModel,
  IUserStatisticsViewModel,
  IVerifyOABSituationViewModel,
} from 'viewModels';

import {
  IDisabledInputModel,
  IJobValuesUpdateByUfCodesInputModel,
  IJobValuesUpdateInputModel,
  INotificationPreferencesUpdateInputModel,
  IPublicCorrespondentSearchInputModel,
  IUpdateDistrictsOfOperationInputModel,
  IVerifyOABSituationInputModel,
  NotificationPreferencesInputModel,
} from 'inputModels';

import { IGoogleMapsResponse, OrderedWithPaginationInputModel } from 'props';
import qs from 'qs';
import dayjs from 'dayjs';
import api from '../services/api/axios';
import { FileUsageType, TutorialType } from '../types/enums';

export interface ILogin {
  email: string;
  password: string;
}
export type ICreate = {
  email: string;
  password: string;
  acceptedPrivacyPolicy: boolean;
  acceptedTermConditions: boolean;
};

type IValidateRegister = {
  document?: string;
  email: string;
};

const findByEmail = (email: string, disabled?: boolean) =>
  new Promise<IUser | undefined>((resolve, reject) => {
    api
      .get(`user/find-by-email/`, { params: { email, disabled } })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const findByDocument = async (document: string) =>
  new Promise<IBaseUserDataSimpleViewModel | undefined>((resolve, reject) => {
    api
      .get(`user/find-by-document/${document}`)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const getPublicProfile = (url: string) =>
  new Promise<IBaseUserViewModel | undefined>((resolve, reject) => {
    api
      .get(`user/${url}`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const urlAlreadyExists = async (url: string) =>
  new Promise<boolean>((resolve, reject) => {
    api
      .get(`user/url-already-exists/${url}`)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const generateUrl = (id: string) =>
  new Promise<string>((resolve) => {
    api.get(`user/generate-url/${id}`).then(({ data }) => resolve(data));
  });
const getNotificationPreferenceByUserId = (id: string | undefined) =>
  new Promise<INotificationPreferencesResponseViewModel>((resolve) => {
    api.get(`user/notification-preference/${id}`).then(({ data }) => resolve(data));
  });
const resetPassword = async (form: FormData) =>
  api.put('user/reset-password', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

const login = async (form: ILogin) =>
  new Promise<ILoginViewModel>((resolve, reject) => {
    api
      .post('user/login', form)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });

const sendWhatsappConfirmation = async (phoneNumber: string) =>
  new Promise<string>((resolve, reject) => {
    api
      .post(`user/send-confirmation-code`, phoneNumber, {
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .then((res) => resolve(res.data))
      .catch((error: any) => reject(error));
  });

const create = (user: ICreate) => {
  return new Promise<string>((resolve, reject) => {
    api
      .post('user', user)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });
};

const update = (user: FormData) => {
  return api.put('user/update-user', user, { headers: { 'Content-Type': 'multipart/form-data' } });
};

const completeRegister = (user: FormData) => {
  return api.put('user/complete-register', user, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

const findUserById = (id: string) => {
  return new Promise<IUserRegister>((resolve, reject) => {
    api
      .get(`user/find-user-by/${id}`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });
};

const updateJobValues = (input: IJobValuesUpdateInputModel) => {
  return new Promise<string>((resolve, reject) => {
    api
      .put('user/update-job-values', input)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });
};
const updateJobValuesByUfCodes = (input: IJobValuesUpdateByUfCodesInputModel) => {
  return new Promise<string>((resolve, reject) => {
    api
      .put('user/update-job-values-by-uf-codes', input)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });
};
const getJobValuesByUserId = (userId: string) => {
  return new Promise<IJobValuesViewModel>((resolve, reject) => {
    api
      .get(`user/get-job-values-by-id/${userId}`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });
};
const getJobValuesByUfCode = (ufCode: number) => {
  return new Promise<IJobValuesViewModel>((resolve, reject) => {
    api
      .get(`user/get-job-values-by/${ufCode}`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });
};
const updatePreferenceNotification = (preferences: INotificationPreferencesUpdateInputModel) => {
  return api.put('user/update-notification-preferences', preferences);
};
const createSolicitation = (solicitation: FormData) => {
  return api.post('user/new-solicitation', solicitation, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
const disableUser = (input: IDisabledInputModel) => {
  return api.put(`user/disabled-user`, input);
};
const enableUser = (email: string) => {
  return api.put(`user/enable-user`, null, { params: { email } });
};
const validateRegister = (body: IValidateRegister) => {
  return api.post('user/validate-register', body);
};
const updateProfilePicture = (file: File, onUploadProgress?: (progress: number) => void) => {
  const controller = new AbortController();
  const promise = new Promise<string>((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    api
      .put(
        'user/update-profile-picture',
        { file },
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            onUploadProgress?.(percentCompleted);
          },
          signal: controller.signal,
        },
      )
      .then(({ data }) => resolve(data))
      .catch(reject);
  });
  return [promise, () => controller.abort.bind(controller)] as [Promise<string>, () => void];
};
const updateDocumentVerificationPicture = (
  file: File,
  onUploadProgress?: (progress: number) => void,
  fileUsageType?: FileUsageType,
  fileId?: string,
) => {
  const controller = new AbortController();
  const promise = new Promise<string>((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    api
      .put(
        'user/update-document-picture',
        { file },
        {
          params: { fileId, fileUsageType },
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = progressEvent.total
              ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
              : 0;
            onUploadProgress?.(percentCompleted);
          },
          signal: controller.signal,
        },
      )
      .then(({ data }) => resolve(data))
      .catch(reject);
  });
  return [promise, () => controller.abort.bind(controller)] as [Promise<string>, () => void];
};
// const getUserDocuments = (id: string) => {
//   return new Promise<IBackendFile[]>((resolve, reject) => {
//     api
//       .get(`user/get-user-documents-by/${id}`)
//       .then(({ data }) => resolve(data))
//       .catch(reject);
//   });
// };
const getPendenciesSolicitation = () =>
  new Promise<IPendencySolicitationViewModel[]>((resolve, reject) => {
    api
      .get(`user/pendencies-solicitation`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });
const deleteProfilePicture = (id: string) => {
  return new Promise((resolve, reject) => {
    api
      .delete(`user/delete-profile-picture/${id}`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });
};

const getCorrespondents = (params: IPublicCorrespondentSearchInputModel) =>
  new Promise<ISearchViewModel<ICorrespondentResultViewModel>>((resolve, reject) => {
    api
      .get(`user/find-correspondents`, { params })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });

const createContactsForNotification = (contacts: NotificationPreferencesInputModel) => {
  return api.post('user/create-contacts-notification', contacts);
};
const updateCorrespondeceForUser = (correspondenceId: string) => {
  return api.put(`user/update-correspondece-user/${correspondenceId}`);
};
const updateDistrictsOfOperationUser = (input: IUpdateDistrictsOfOperationInputModel) => {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`user/district-update`, input)
      .then(() => resolve())
      .catch(reject);
  });
};

const getBaseUserIncompleteRegister = () => {
  return new Promise<IUserRegister>((resolve, reject) => {
    api
      .get(`user/get-incomplete-register`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });
};
const documentAlreadyExists = async (document: string) => {
  return new Promise<boolean>((resolve, reject) => {
    api
      .get(`user/document-already-exists/${document}`)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};
const emailAlreadyExists = async (email: string) => {
  return new Promise<boolean>((resolve, reject) => {
    api
      .get(`user/email-already-exists/${email}`)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};

const getFinancialReportByUser = (input: OrderedWithPaginationInputModel) => {
  return new Promise<IFinancialReportViewModel[]>((resolve, reject) => {
    api
      .get(
        `user/financial-report-by-user?${input?.orderBy
          ?.map((o, i) => `orderBy[${i}].key=${o.key}&orderBy[${i}].direction=${o.direction}`)
          .join('&')}`,

        {
          params: input,
          paramsSerializer: (params) => qs.stringify(params),
        },
      )
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};

const getLocationByAddress = async (address: string) => {
  return new Promise<IGoogleMapsResponse>((resolve, reject) => {
    api
      .get(`user/get-location/${address}`)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};

const verifySituationOAB = async (input: IVerifyOABSituationInputModel) => {
  return new Promise<IVerifyOABSituationViewModel>((resolve, reject) => {
    api
      .get(`user/verify-oab`, { params: input, paramsSerializer: (params) => qs.stringify(params) })
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};

const confirmUserRole = async () => {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`user/confirm-user-role`)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};

const insertUserInvite = async (email: string) => {
  return new Promise<void>((resolve, reject) => {
    api
      .post(`user/invite-user?email=${encodeURIComponent(email)}`)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};

const getDashboardStatistics = async () => {
  return new Promise<IDashboardStatisticsViewModal>((resolve, reject) => {
    api
      .get(`user/dashboard-statistics`)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};

const getUserStatistics = async (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => {
  return new Promise<IUserStatisticsViewModel[]>((resolve, reject) => {
    api
      .get(
        `user/users-statistics?startDate=${startDate.toISOString().split('T')[0]}&endDate=${endDate.toISOString().split('T')[0]
        }`,
      )
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};

const completeTutorialType = async (type: TutorialType) => {
  return new Promise<void>((resolve, reject) => {
    api
      .put(`user/complete-tutorial-type/${type}`)
      .then(({ data }) => resolve(data))
      .catch(() => reject());
  });
};
const legalTrainingSigin = () => {
  return new Promise<string>((resolve, reject) => {
    api
      .get(`user/external-signin`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });
};
const timeSheetSigin = () => {
  return new Promise<string>((resolve, reject) => {
    api
      .get(`user/external-signin-timesheet`)
      .then(({ data }) => resolve(data))
      .catch(reject);
  });
};

export const UserController = {
  getCorrespondents,
  findByEmail,
  getPublicProfile,
  findByDocument,
  urlAlreadyExists,
  findUserById,
  generateUrl,
  getNotificationPreferenceByUserId,
  sendWhatsappConfirmation,
  resetPassword,
  login,
  create,
  update,
  completeRegister,
  updatePreferenceNotification,
  updateJobValues,
  getJobValuesByUfCode,
  updateJobValuesByUfCodes,
  getJobValuesByUserId,
  disableUser,
  enableUser,
  createSolicitation,
  createContactsForNotification,
  validateRegister,
  updateProfilePicture,
  updateDocumentVerificationPicture,
  //getUserDocuments,
  deleteProfilePicture,
  getPendenciesSolicitation,
  updateCorrespondeceForUser,
  getBaseUserIncompleteRegister,
  documentAlreadyExists,
  emailAlreadyExists,
  getFinancialReportByUser,
  getLocationByAddress,
  updateDistrictsOfOperationUser,
  verifySituationOAB,
  confirmUserRole,
  insertUserInvite,
  getDashboardStatistics,
  getUserStatistics,
  completeTutorialType,
  legalTrainingSigin,
  timeSheetSigin,
};
